import { NormalizeOAS, OASRequestParams, OASResponse } from 'fets';
import type assetLibraryOpenApi from '../asset-library-open-api';
import { AssetLibraryFetsApiClient } from '../asset-library-fets-client';
import { assertNever } from '../utils';
import { Guid } from '../../api/public/utils/Guid';
import { AssetDetail } from './getAssetDetail';

const PATH = '/api/v2/asset/company/{companyId}/asset/{assetId}';

type Params = {
  companyId: Guid;
  assetId: Guid;
  name: string;
};

type Result = AssetDetail;

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<Result>;

export const createRenameAsset = (apiClient: AssetLibraryFetsApiClient): ApiFunction => {
  const renameAsset = async (params: Params, signal?: AbortSignal): Promise<Result> => {
    const response = await apiClient[PATH].put({
      ...convertToRequest(params),
      ...(signal && { signal }),
    });

    return convertResponse(response);
  };

  return renameAsset;
};

type Request = OASRequestParams<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'put'>;
type Response = OASResponse<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'put'>;

const convertToRequest = (params: Params): Request => ({
  params: { companyId: params.companyId, assetId: params.assetId },
  json: { name: params.name },
});

const convertResponse = async (response: Response): Promise<Result> => {
  const responseStatus = response.status;
  if (responseStatus !== 200) {
    return assertNever(responseStatus);
  }

  const data = await response.json();
  const result: Result = {
    id: data.id,
    name: data.name,
    size: data.size,
    uploadedAt: Number(data.uploadedAt),
    state: data.state,
    mediaUrl: data.mediaUrl,
    thumbnailUrl: data.thumbnailUrl ?? null,
    dimensions: data.dimensions ?? null,
    attribution: data.attribution ?? null,
  };
  return result;
};
